import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import NewsletterList from './newsletter/components/NewsletterList';
import Subscriptions from './subscriptions/components/Subscriptions';
import Articles from './articles/components/Articles';
import PaymentList from './payment/components/PaymentList';
import AuthorAndTopicList from './newsletter/components/AuthorAndTopicList';
import EmailValidation from './auth/components/EmailValidation';
import UnknownUrl from './common/components/UnknownUrl';
import SettingsPage from './settings/components/SettingsPage';
import MyAccountNavigation from './my-account-navigation/MyAccountNavigation';
import { getUserInfo } from './settings/services/UserServices';
import { UserInfo } from './settings/types/User';
import { Article } from './articles/types/Article';
import { getArticlesByIdArray, removeBookmarkedArticle } from './articles/services/ArticleService';
import { useTranslations } from './localization/useTranslations';
import OnboardingModal from './components/onboarding/OnboardingModal';

function App() {
  const [userInfo, setUserInfo] = useState<UserInfo>({
    email: '-',
    firstName: '',
    lastName: '-',
    avatarUrl: '',
    userId: '',
  });

  const [isUserInfoLoading, setIsUserInfoLoading] = useState(true);

  const [isEmailVerified, setIsEmailVerified] = useState(window.crm_user?.user?.email_validated_at ? true : false);

  const [articles, setArticles] = useState<Article[]>([]);
  const [isArticlesLoading, setIsArticlesLoading] = useState<boolean>(true);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState<boolean>(false);

  const location = useLocation();
  const translations = useTranslations();

  useEffect(() => {
    setIsEmailVerified(window.crm_user?.user?.email_validated_at ? true : false);
  }, [location]);

  const fetchUserInfo = async () => {
    try {
      setIsUserInfoLoading(true);

      await getUserInfo().then((response) => {
        setUserInfo({
          email: response.user.email,
          firstName: response.user.first_name,
          lastName: response.user.last_name,
          avatarUrl: response.user_meta.avatar_url || window.defaultUserAvatar,
          userId: response.user.id,
        });
      });
    } catch (err) {
      console.error(err);
    } finally {
      setIsUserInfoLoading(false);
    }
  };

  const fetchArticles = async () => {
    try {
      const bookmarks = window?.crm_user?.user_meta?.posts_bookmarks;
      if (!bookmarks) {
        setArticles([]);
        return;
      }

      const bookmarkIds = Object.keys(bookmarks);
      const result = await getArticlesByIdArray(bookmarkIds);
      const fetchedArticles = result
        .map((element) => {
          return {
            id: element.id.toString(),
            savedAt: bookmarks[element.id],
            title: element.title,
            tagLink: element.tagLink,
            tagName: element.tagName,
            authorName: element.authorName,
            authorArchiveLink: element.authorArchiveLink,
            authorLink: element.authorLink,
            publishDate: element.publishDate,
            readTime: element.readTime,
            imageLink: element.imageLink,
            link: element.link,
            isVisible: true,
          };
        })
        .filter((article) => article.isVisible);

      setArticles(fetchedArticles);
    } catch (error) {
      console.error('Error fetching saved articles:', error);
    } finally {
      setIsArticlesLoading(false);
    }
  };

  const handleUserInfoChange = (updatedInfo: UserInfo) => {
    setUserInfo((prev) => ({
      ...prev,
      ...updatedInfo,
    }));

    if (updatedInfo.avatarUrl) {
      setUserInfo((prev: UserInfo | null) => ({
        ...prev!,
        avatarUrl: updatedInfo.avatarUrl,
      }));
    }
  };

  const handleRemoveArticle = async (id: string) => {
    try {
      await removeBookmarkedArticle(id);
      setArticles((prevArticles) =>
        prevArticles.map((article) => (article.id === id ? { ...article, isVisible: false } : article))
      );
    } catch (error) {
      console.error('Error removing article:', error);
    }
  };

  useEffect(() => {
    fetchUserInfo();
    fetchArticles();

    //const isOnboardingFinished = window.crm_user?.user_meta?.is_onboarding_finished === '1';

    setIsOnboardingOpen(false); // Temporarily set to false for now
  }, []);

  const { route } = translations;

  return (
    <>
      <MyAccountNavigation userInfo={userInfo} isUserInfoLoading={isUserInfoLoading} />
      <Routes>
        <Route
          path="/"
          element={
            <Articles
              articles={articles}
              isArticlesLoading={isArticlesLoading}
              handleRemoveArticle={handleRemoveArticle}
            />
          }
        />
        <Route path={`/${route?.followedAuthorsAndTopics}`} element={<AuthorAndTopicList />} />
        <Route
          path={`/${route?.savedArticles}`}
          element={
            <Articles
              articles={articles}
              isArticlesLoading={isArticlesLoading}
              handleRemoveArticle={handleRemoveArticle}
            />
          }
        />
        <Route path={`/${route?.myNewsletters}`} element={<NewsletterList />} />
        <Route path={`/${route?.myOrders}`} element={<PaymentList />} />
        <Route path={`/${route?.subscriptions}`} element={<Subscriptions />} />
        <Route
          path={`/${route?.settings}`}
          element={
            <SettingsPage userInfo={userInfo} setUserInfo={setUserInfo} handleUserInfoChange={handleUserInfoChange} />
          }
        />
        {!isEmailVerified && <Route path="/email-validation" element={<EmailValidation />} />}
        <Route path="*" element={<UnknownUrl />} />
      </Routes>

      {isOnboardingOpen && (
        <OnboardingModal isOnboardingOpen={isOnboardingOpen} setIsOnboardingOpen={setIsOnboardingOpen} />
      )}
    </>
  );
}

export default App;
